/**
 * CodePanel Component
 * 
 * Displays a file viewer with diff support.
 * Supports "Tabbed" (Sidebar) and "Stacked" views.
 * Features a hierarchical file tree navigation.
 * Part of the json-render component system.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { Button, Input, Select, SelectTrigger, SelectContent, SelectItem } from '@pagespeedify/ui';
import { html as diff2html } from 'diff2html';
import 'diff2html/bundles/css/diff2html.min.css';
import './codepanel-diff.css';
import {
    FileCode,
    FilePlus,
    FileX,
    FileDiff,
    Download,
    Copy,
    Check,
    PanelLeft,  // Sidebar/Tabbed icon
    ChevronRight,
    ChevronDown,
    FolderTree,
    Search,
    FileArchive,
    X,
    Maximize2,
    Minimize2,
    Image as ImageIcon
} from 'lucide-react';
import { cn } from '@pagespeedify/ui';
import { useTheme } from '../ThemeProvider';

import type { CodePanelProps, CodePanelFile } from '../../types/json-render';

interface CodePanelComponentProps extends CodePanelProps {
    onDownload?: (file: CodePanelFile) => void;
    onFileSelect?: (path: string) => void;
    onDownloadRepo?: () => void;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    viewMode?: 'tabbed' | 'stacked';
    onViewModeChange?: (mode: 'tabbed' | 'stacked') => void;
}

interface FileTreeNode {
    name: string;
    path: string;
    type: 'file' | 'directory';
    children?: FileTreeNode[];
    status?: CodePanelFile['status'];
    fileIndex?: number;
}

const IMAGE_EXTENSIONS = new Set([
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
    '.bmp',
    '.svg',
    '.ico',
    '.avif'
]);

function getFileExtension(pathOrName: string): string {
    const dot = pathOrName.lastIndexOf('.');
    return dot >= 0 ? pathOrName.slice(dot).toLowerCase() : '';
}

function isImageFilePath(pathOrName: string): boolean {
    return IMAGE_EXTENSIONS.has(getFileExtension(pathOrName));
}

function isImageDataUrl(value?: string): value is string {
    if (!value) return false;
    return /^data:image\/[a-zA-Z0-9.+-]+;base64,/i.test(value.trim());
}

function isHttpUrl(value?: string): value is string {
    if (!value) return false;
    return /^https?:\/\/\S+/i.test(value.trim());
}

function isLikelyBinaryText(value?: string): boolean {
    if (!value) return false;
    if (value.includes('\u0000') || value.includes('\ufffd')) return true;
    const sample = value.slice(0, 2000);
    if (!sample) return false;
    let controlCount = 0;
    for (let i = 0; i < sample.length; i += 1) {
        const code = sample.charCodeAt(i);
        if (code < 32 && code !== 9 && code !== 10 && code !== 13) {
            controlCount += 1;
        }
    }
    return controlCount / sample.length > 0.05;
}

function resolveImagePreviewSource(file: CodePanelFile): string | undefined {
    const candidates = [file.content, file.contentPreview]
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean);

    for (const candidate of candidates) {
        if (isImageDataUrl(candidate) || isHttpUrl(candidate)) {
            return candidate;
        }
    }

    // SVG content can be previewed even when provided as raw markup.
    if (getFileExtension(file.path) === '.svg') {
        const rawSvg = candidates.find((value) => value.startsWith('<svg'));
        if (rawSvg) {
            return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(rawSvg)}`;
        }
    }

    return undefined;
}

function FileStatusIcon({ status, className }: { status?: CodePanelFile['status']; className?: string }) {
    const classes = className || "h-3.5 w-3.5";
    switch (status) {
        case 'added':
            return <FilePlus className={cn(classes, "text-green-600")} />;
        case 'deleted':
            return <FileX className={cn(classes, "text-destructive")} />;
        case 'modified':
            return <FileDiff className={cn(classes, "text-amber-500")} />;
        default:
            return <FileCode className={cn(classes, "text-muted-foreground")} />;
    }
}

/**
 * Convert a legacy EDIT_FILE diff format (<<<< ORIGINAL / ==== / >>>> MODIFIED)
 * into a standard unified diff format that diff2html can render.
 */
function convertLegacyDiff(raw: string, filePath: string): string {
    const match = raw.match(/<<<< ORIGINAL\n([\s\S]*?)\n====\n([\s\S]*?)\n>>>> MODIFIED/);
    if (!match) return raw; // Already unified format or unknown

    const original = match[1];
    const modified = match[2];
    const origLines = original.split('\n');
    const modLines = modified.split('\n');

    return [
        `diff --git a/${filePath} b/${filePath}`,
        `--- a/${filePath}`,
        `+++ b/${filePath}`,
        `@@ -1,${origLines.length} +1,${modLines.length} @@`,
        ...origLines.map(l => `-${l}`),
        ...modLines.map(l => `+${l}`),
    ].join('\n');
}

/**
 * DiffViewer - renders diffs using diff2html for rich visualization.
 * Handles both standard unified diffs and legacy EDIT_FILE format.
 */
function DiffViewer({ diff, filePath, theme }: { diff: string; filePath: string; theme: 'dark' | 'light' }) {
    const htmlContent = useMemo(() => {
        try {
            // Convert legacy format if needed
            const unifiedDiff = diff.includes('<<<< ORIGINAL')
                ? convertLegacyDiff(diff, filePath)
                : diff;

            return diff2html(unifiedDiff, {
                drawFileList: false,
                matching: 'words',
                outputFormat: 'line-by-line',
                diffStyle: 'word',
            });
        } catch {
            // Fallback: render raw diff text
            return null;
        }
    }, [diff, filePath]);

    if (!htmlContent) {
        // Fallback for malformed diffs
        return (
            <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-words text-foreground/90 overflow-x-auto">
                {diff}
            </pre>
        );
    }

    return (
        <div
            className={cn(
                'diff2html-wrapper ps-codepanel-diff text-xs',
                theme === 'dark' ? 'd2h-dark-color-scheme' : ''
            )}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
    );
}

// Sub-component for rendering a single file's content
function FileContent({ file, onCopy, copied, pathRef, theme }: { file: CodePanelFile; onCopy: (text: string) => void; copied: boolean; pathRef?: (el: HTMLDivElement | null) => void; theme: 'dark' | 'light' }) {
    const textContent = file.content || file.contentPreview || '';
    const isImageFile = isImageFilePath(file.path || file.name || '');
    const imagePreviewSrc = isImageFile ? resolveImagePreviewSource(file) : undefined;
    const hasBinaryImageDiff = isImageFile && !!file.diff && isLikelyBinaryText(file.diff);
    const hasContent = Boolean(textContent || file.diff || imagePreviewSrc || isImageFile);
    const copyPayload = textContent || file.diff || '';
    const canCopy = !isImageFile && copyPayload.length > 0;

    return (
        <div className="relative group border-b last:border-b-0" ref={pathRef}>
            {canCopy && (
                <div className="absolute right-3 top-3 z-10 opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 gap-1 bg-background/70 backdrop-blur-sm border-border/70 text-muted-foreground hover:text-foreground hover:bg-background/90 shadow-sm"
                        onClick={() => onCopy(copyPayload)}
                    >
                        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                        <span className="text-xs">Copy</span>
                    </Button>
                </div>
            )}
            {hasContent ? (
                <div className="min-w-0 bg-background">
                    {isImageFile ? (
                        imagePreviewSrc ? (
                            <div className="p-5">
                                <div className="rounded-lg border border-border/60 bg-muted/5 p-3">
                                    <img
                                        src={imagePreviewSrc}
                                        alt={file.name || file.path}
                                        className="max-h-[70vh] max-w-full object-contain mx-auto rounded"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="p-10 text-center bg-muted/5 border-t">
                                <ImageIcon className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                                <p className="text-sm font-medium text-foreground">Binary image change detected</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {hasBinaryImageDiff
                                        ? 'This payload contains binary diff text. Preview is unavailable for this run.'
                                        : 'No image preview payload is available for this file.'}
                                </p>
                            </div>
                        )
                    ) : file.diff ? (
                        <DiffViewer diff={file.diff} filePath={file.path} theme={theme} />
                    ) : textContent ? (
                        <pre className="p-5 text-xs font-mono whitespace-pre-wrap break-words text-foreground/90 leading-relaxed overflow-x-auto">
                            {textContent}
                        </pre>
                    ) : null}
                </div>
            ) : (
                <div className="p-12 text-center text-sm text-muted-foreground bg-muted/5">
                    No content available for this file.
                </div>
            )}
        </div>
    );
}

// Recursive Tree Item Component
const TreeItem = ({ node, activeIndex, onSelect, expandedFolders, toggleFolder }: any) => {
    const isActive = node.type === 'file' && node.fileIndex === activeIndex;
    const isExpanded = expandedFolders.has(node.path);

    if (node.type === 'directory') {
        return (
            <div className="mb-0.5">
                <button
                    onClick={() => toggleFolder(node.path)}
                    className="flex items-center gap-1.5 w-full px-2 py-1.5 text-left text-xs hover:bg-muted/80 rounded-md transition-colors group"
                >
                    {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                    <FolderTree className="h-3.5 w-3.5 text-blue-500/60 group-hover:text-blue-500 transition-colors" />
                    <span className="truncate font-medium">{node.name}</span>
                </button>
                {isExpanded && node.children && (
                    <div className="ml-2.5 pl-2">
                        {node.children.map((child: any) => (
                            <TreeItem
                                key={child.path}
                                node={child}
                                activeIndex={activeIndex}
                                onSelect={onSelect}
                                expandedFolders={expandedFolders}
                                toggleFolder={toggleFolder}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <button
            onClick={() => onSelect(node.fileIndex)}
            className={cn(
                "flex items-center gap-2 w-full px-2 py-1.5 text-left text-xs rounded-md transition-colors mb-0.5",
                isActive ? "bg-primary/10 text-primary font-semibold shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
        >
            <div className="w-3.5" /> {/* Align with folder arrow */}
            <FileStatusIcon status={node.status} className={cn("h-3.5 w-3.5", isActive ? "text-primary" : "")} />
            <span className="truncate">{node.name}</span>
        </button>
    );
};

export default function CodePanel({
    files,
    activeFileIndex = 0,
    showDownload = true,
    onDownload,
    onFileSelect,
    onDownloadRepo,
    isExpanded = false,
    onToggleExpand,
    viewMode: controlledViewMode,
    onViewModeChange,
}: CodePanelComponentProps) {
    const { theme } = useTheme();
    const [activeIndex, setActiveIndex] = useState(activeFileIndex);
    const [localViewMode, setLocalViewMode] = useState<'tabbed' | 'stacked'>('tabbed');
    const viewMode = controlledViewMode ?? localViewMode;

    const setViewMode = (mode: 'tabbed' | 'stacked') => {
        setLocalViewMode(mode);
        onViewModeChange?.(mode);
    };
    // isSidebarOpen is now controlled by isExpanded logic mostly, but we can keep local toggle if expanded
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [copied, setCopied] = useState(false);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [expandedStacked, setExpandedStacked] = useState<Set<string>>(new Set(files?.map(f => f.path)));

    const stackedRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const scrollAreaRef = useRef<HTMLDivElement>(null);

    // Synchronize activeIndex when prop changes
    useEffect(() => {
        if (activeFileIndex !== activeIndex) {
            setActiveIndex(activeFileIndex);
        }
    }, [activeFileIndex]);

    useEffect(() => {
        console.info('CODEPANEL_THEME_MODE_APPLIED', { theme, viewMode });
    }, [theme, viewMode]);

    // Build hierarchical tree
    const fileTree = useMemo(() => {
        const root: FileTreeNode[] = [];
        const filteredFiles = files.filter(f =>
            f.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (f.name || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

        filteredFiles.forEach((file) => {
            const originalIndex = files.indexOf(file);
            const parts = file.path.split('/');
            let currentLevel = root;

            parts.forEach((part, partIndex) => {
                const path = parts.slice(0, partIndex + 1).join('/');
                const isLast = partIndex === parts.length - 1;

                let node = currentLevel.find(n => n.name === part);
                if (!node) {
                    node = {
                        name: part,
                        path: path,
                        type: isLast ? 'file' : 'directory',
                        status: isLast ? file.status : undefined,
                        fileIndex: isLast ? originalIndex : undefined,
                        children: isLast ? undefined : []
                    };
                    currentLevel.push(node);
                    currentLevel.sort((a, b) => {
                        if (a.type === b.type) return a.name.localeCompare(b.name);
                        return a.type === 'directory' ? -1 : 1;
                    });
                }
                if (!isLast && node.children) {
                    currentLevel = node.children;
                }
            });
        });

        // Auto-expand parents if searching
        if (searchTerm) {
            const toExpand = new Set<string>();
            const walk = (nodes: FileTreeNode[]) => {
                nodes.forEach(n => {
                    if (n.type === 'directory' && n.children) {
                        toExpand.add(n.path);
                        walk(n.children);
                    }
                });
            };
            walk(root);
            setExpandedFolders(toExpand);
        }

        return root;
    }, [files, searchTerm]);

    const activeFile = files[activeIndex] || files[0];

    const handleCopy = async (text: string) => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const toggleFolder = (path: string) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpandedFolders(newExpanded);
    };

    const handleFileSelectInternal = (index: number) => {
        setActiveIndex(index);
        const file = files[index];
        if (onFileSelect) onFileSelect(file.path);

        if (viewMode === 'stacked') {
            const ref = stackedRefs.current[file.path];
            if (ref) {
                ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            // Ensure it's expanded in stacked view
            const newExpanded = new Set(expandedStacked);
            newExpanded.add(file.path);
            setExpandedStacked(newExpanded);
        }
    };

    const toggleStackedExpansion = (path: string) => {
        const newExpanded = new Set(expandedStacked);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpandedStacked(newExpanded);
    };

    return (
        <div className="flex flex-col h-full w-full max-w-full overflow-hidden bg-background border-0">
            {/* Compact Toolbar (tabs + controls on one row to maximize content space) */}
            <div className="flex items-center justify-between gap-2 px-2 py-1 bg-muted/10 shrink-0 min-h-9">
                <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                    {/* Toggle Sidebar Button - Only show if expanded */}
                    {isExpanded && (
                        <Button
                            variant={isSidebarOpen ? 'secondary' : 'ghost'}
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            title="Toggle Explorer"
                        >
                            <PanelLeft className="h-3.5 w-3.5" />
                        </Button>
                    )}

                    {viewMode === 'tabbed' ? (
                        <div className="min-w-0 flex-1 overflow-x-auto no-scrollbar scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            <div className="flex items-stretch min-w-max gap-0.5">
                                {files.map((file, index) => (
                                    <button
                                        key={file.path}
                                        onClick={() => handleFileSelectInternal(index)}
                                        className={cn(
                                            "flex items-center gap-1.5 px-2.5 h-7 text-xs transition-colors whitespace-nowrap min-w-[90px] max-w-[190px] rounded-sm",
                                            index === activeIndex
                                                ? "bg-background text-primary font-medium border border-primary/40"
                                                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground bg-muted/20"
                                        )}
                                        title={file.path}
                                    >
                                        <FileStatusIcon status={file.status} />
                                        <span className="truncate">{file.name || file.path.split('/').pop()}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="min-w-0 flex-1 text-xs text-muted-foreground truncate px-1">
                            {activeFile?.path || `${files.length} files`}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                    {/* View Switcher Select */}
                    <Select
                        value={viewMode}
                        onValueChange={(val: 'tabbed' | 'stacked') => setViewMode(val)}
                    >
                        <SelectTrigger className="h-7 w-[96px] text-xs px-2">
                            <div className="flex items-center gap-2">
                                <span>{viewMode === 'tabbed' ? 'Tabbed' : 'Stacked'}</span>
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="tabbed">
                                <div className="flex items-center gap-2">
                                    <span>Tabbed View</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="stacked">
                                <div className="flex items-center gap-2">
                                    <span>Stacked View</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Expand/Collapse Toggle */}
                    {onToggleExpand && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={onToggleExpand}
                            title={isExpanded ? "Collapse View" : "Expand View"}
                        >
                            {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                        </Button>
                    )}

                    {/* Repo Actions */}
                    <div className="flex items-center gap-1 pl-1.5 border-l ml-1.5">
                        {onDownloadRepo && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 gap-1.5 text-xs font-medium"
                                onClick={onDownloadRepo}
                                title="Download ZIP"
                            >
                                <FileArchive className="h-3.5 w-3.5" />
                                <span className="hidden md:inline">ZIP</span>
                            </Button>
                        )}
                        {showDownload && onDownload && (
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => onDownload(activeFile)}
                                title="Download Active File"
                            >
                                <Download className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden flex relative">
                {/* Explorer Sidebar - ONLY in Expanded Mode */}
                {isExpanded && isSidebarOpen && (
                    <div className="w-1/4 min-w-[220px] max-w-[350px] h-full flex flex-col bg-muted/5 shrink-0">
                        <div className="p-3">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                                <Input
                                    placeholder="Filter files..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-8 h-8 text-xs bg-background/50"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 h-full overflow-y-auto no-scrollbar">
                            <div className="p-2.5">
                                {fileTree.length > 0 ? (
                                    fileTree.map(node => (
                                        <TreeItem
                                            key={node.path}
                                            node={node}
                                            activeIndex={activeIndex}
                                            onSelect={handleFileSelectInternal}
                                            expandedFolders={expandedFolders}
                                            toggleFolder={toggleFolder}
                                        />
                                    ))
                                ) : (
                                    <div className="text-center py-10">
                                        <Search className="h-8 w-8 mx-auto text-muted-foreground/20 mb-2" />
                                        <p className="text-xs text-muted-foreground">No matching files</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Content Viewer */}
                <div className="flex-1 min-w-0 min-h-0 bg-background flex flex-col">
                    {viewMode === 'tabbed' ? (
                        <div className="flex flex-col h-full overflow-hidden">
                            <div className="flex-1 h-full overflow-y-auto no-scrollbar">
                                <FileContent file={activeFile} onCopy={handleCopy} copied={copied} theme={theme} />
                            </div>
                        </div>
                    ) : (
                        /* Stacked View */
                        <div className="flex-1 h-full overflow-y-auto no-scrollbar" ref={scrollAreaRef}>
                            <div className="p-6 space-y-8 max-w-5xl mx-auto w-full">
                                {files.map((file, index) => (
                                    <div
                                        key={file.path}
                                        className={cn(
                                            "rounded-xl overflow-hidden bg-background shadow-sm transition-all",
                                            activeIndex === index ? "ring-2 ring-primary/20" : "hover:bg-muted/5"
                                        )}
                                        onClick={() => setActiveIndex(index)}
                                    >
                                        {/* Stacked File Header */}
                                        <div
                                            className={cn(
                                                "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                                                expandedStacked.has(file.path) ? "bg-muted/30" : "bg-muted/10"
                                            )}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleStackedExpansion(file.path);
                                                setActiveIndex(index);
                                            }}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                {expandedStacked.has(file.path) ? (
                                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                )}
                                                <FileStatusIcon status={file.status} />
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-semibold truncate leading-none mb-1">
                                                        {file.name || file.path.split('/').pop()}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground font-mono truncate">
                                                        {file.path}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {file.status && (
                                                    <span className={cn(
                                                        "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border",
                                                        file.status === 'added'
                                                            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                                                            : file.status === 'deleted'
                                                                ? "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30"
                                                                : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
                                                    )}>
                                                        {file.status}
                                                    </span>
                                                )}
                                                {showDownload && onDownload && (
                                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onDownload(file); }}>
                                                        <Download className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        {/* Stacked File Content */}
                                        {expandedStacked.has(file.path) && (
                                            <FileContent
                                                file={file}
                                                onCopy={handleCopy}
                                                copied={copied}
                                                theme={theme}
                                                pathRef={(el) => stackedRefs.current[file.path] = el}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
